import React, { useEffect, useState } from 'react';
import { Search, Truck, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { inr, cx } from '@/lib/format';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

// Mirrors backend STATUS_TRANSITIONS — only valid next states are offered
const STATUS_FLOW = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['processing', 'cancelled'],
  processing: ['shipped', 'cancelled'],
  shipped: ['out_for_delivery', 'delivered'],
  out_for_delivery: ['delivered'],
  delivered: ['return_requested'],
  return_requested: ['returned', 'delivered'],
  returned: ['refund_pending', 'refunded'],
  cancelled: ['refund_pending', 'refunded'],
  refund_pending: ['refunded'],
  refunded: [],
};
const ALL_STATUSES = Object.keys(STATUS_FLOW);
const DESTRUCTIVE = new Set(['cancelled', 'returned', 'refunded']);
const PAYMENT_STATUSES = ['pending', 'paid', 'cod', 'failed', 'refund_pending', 'refunded'];

const BADGE = {
  pending: 'bg-amber-100 text-amber-700', confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-indigo-100 text-indigo-700', shipped: 'bg-cyan-100 text-cyan-700',
  out_for_delivery: 'bg-teal-100 text-teal-700', delivered: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700', return_requested: 'bg-orange-100 text-orange-700',
  returned: 'bg-orange-100 text-orange-800', refund_pending: 'bg-purple-100 text-purple-700',
  refunded: 'bg-gray-200 text-gray-700',
};
const nice = (s) => (s || '').replace(/_/g, ' ');
const StatusBadge = ({ s }) => <span className={cx('rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize', BADGE[s] || 'bg-gray-100 text-gray-600')}>{nice(s)}</span>;

const fieldCls = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';

export default function AdminOrders() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payFilter, setPayFilter] = useState('all');
  const [ship, setShip] = useState({ courier: '', tracking_number: '', shipped_at: '', estimated_delivery: '' });
  const [savingShip, setSavingShip] = useState(false);
  const [notifLogs, setNotifLogs] = useState([]);

  const loadNotifications = (orderNumber) => {
    if (!orderNumber) return;
    api.get('/admin/notifications', { params: { order_number: orderNumber, limit: 30 } })
      .then((r) => setNotifLogs(r.data?.items || [])).catch(() => setNotifLogs([]));
  };

  const load = () => {
    const params = { limit: 50 };
    if (q.trim()) params.q = q.trim();
    if (statusFilter !== 'all') params.status = statusFilter;
    if (payFilter !== 'all') params.payment = payFilter;
    api.get('/admin/orders', { params }).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, statusFilter, payFilter]);

  const openDetail = (o) => {
    setDetail(o); setStatusNote(''); setNote('');
    setShip({ courier: '', tracking_number: '', shipped_at: '', estimated_delivery: '', ...(o.shipping_info || {}) });
    setNotifLogs([]);
    loadNotifications(o.order_number);
  };

  const setStatus = async (id, status, withNote = '') => {
    if (DESTRUCTIVE.has(status) && !window.confirm(`Mark this order as ${nice(status)}? ${status === 'cancelled' || status === 'returned' ? 'Stock will be restored automatically.' : ''}`)) return;
    try {
      const { data: fresh } = await api.put(`/admin/orders/${id}/status`, { status, note: withNote });
      toast.success(`Order marked ${nice(status)}`);
      load();
      if (detail?.id === id) { setDetail(fresh); setStatusNote(''); loadNotifications(fresh.order_number); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const setPaymentStatus = async (id, payment_status) => {
    try { const { data: fresh } = await api.put(`/admin/orders/${id}/payment`, { payment_status }); toast.success('Payment status updated'); load(); if (detail?.id === id) { setDetail(fresh); loadNotifications(fresh.order_number); } }
    catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const saveShipping = async () => {
    if (!detail) return;
    setSavingShip(true);
    try {
      const { data: fresh } = await api.put(`/admin/orders/${detail.id}/shipping`, ship);
      setDetail(fresh); toast.success('Shipping details saved'); load(); loadNotifications(fresh.order_number);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); } finally { setSavingShip(false); }
  };

  const addNote = async () => {
    const text = note.trim(); if (!text || !detail) return;
    try { const { data: fresh } = await api.post(`/admin/orders/${detail.id}/notes`, { note: text }); setDetail(fresh); setNote(''); toast.success('Note added'); load(); }
    catch { toast.error('Failed'); }
  };

  const nextOptions = (s) => STATUS_FLOW[s] || [];

  return (
    <div>
      <h1 className="text-2xl font-semibold">Orders</h1>
      <p className="text-sm text-muted-foreground">Full lifecycle: pending → confirmed → processing → shipped → out for delivery → delivered, plus cancellations, returns & refunds.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order #, name, email, phone…" className={cx(fieldCls, 'pl-8')} data-testid="admin-orders-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="admin-orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {ALL_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{nice(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={payFilter} onValueChange={setPayFilter}>
          <SelectTrigger className="h-9 w-36 text-xs" data-testid="admin-orders-payment-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            <SelectItem value="cod">COD</SelectItem>
            <SelectItem value="razorpay">Razorpay</SelectItem>
          </SelectContent>
        </Select>
        {data && <span className="text-xs text-muted-foreground">{data.total} order{data.total === 1 ? '' : 's'}</span>}
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-orders-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground"><tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Total</th><th className="p-3">Payment</th><th className="p-3">Status</th><th className="p-3">Move to</th><th className="p-3"></th></tr></thead>
          <tbody>
            {data?.items?.map((o) => (
              <tr key={o.id} className="border-t border-border">
                <td className="p-3 font-medium">{o.order_number}<br /><span className="text-[11px] font-normal text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span></td>
                <td className="p-3">{o.address?.full_name}<br /><span className="text-xs text-muted-foreground">{o.address?.phone}</span></td>
                <td className="p-3 font-semibold">{inr(o.total)}</td>
                <td className="p-3"><span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 text-xs uppercase">{o.payment_method}</span><br /><span className="text-[10px] capitalize text-muted-foreground">{nice(o.payment_status || 'pending')}</span></td>
                <td className="p-3"><StatusBadge s={o.status} /></td>
                <td className="p-3">
                  {nextOptions(o.status).length ? (
                    <Select value="" onValueChange={(v) => setStatus(o.id, v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid="admin-order-status"><SelectValue placeholder="Next status…" /></SelectTrigger>
                      <SelectContent>{nextOptions(o.status).map((s) => <SelectItem key={s} value={s} className="capitalize">{nice(s)}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-[11px] text-muted-foreground">Final state</span>}
                </td>
                <td className="p-3"><button onClick={() => openDetail(o)} className="text-sm font-semibold text-[hsl(var(--primary))]" data-testid="admin-order-view">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No orders found.</p>}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Order {detail?.order_number} {detail && <StatusBadge s={detail.status} />}</DialogTitle>
            <DialogDescription className="sr-only">Order detail and management</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-5 text-sm">
              {/* Status management */}
              <div className="rounded-xl border border-border p-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Update status</p>
                {nextOptions(detail.status).length ? (
                  <div className="mt-2 space-y-2">
                    <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Optional note for the timeline (visible internally)" className={fieldCls} data-testid="admin-order-status-note" />
                    <div className="flex flex-wrap gap-2">
                      {nextOptions(detail.status).map((s) => (
                        <button key={s} onClick={() => setStatus(detail.id, s, statusNote)} data-testid={`admin-order-move-${s}`} className={cx('rounded-full px-4 py-2 text-xs font-semibold capitalize', DESTRUCTIVE.has(s) ? 'border border-red-200 bg-red-50 text-red-700' : 'bg-[hsl(var(--primary))] text-white')}>
                          {nice(s)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : <p className="mt-1 text-xs text-muted-foreground">This order is in a final state.</p>}
              </div>

              {/* Payment */}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Payment · {detail.payment_method?.toUpperCase()}</p>
                  <Select value={detail.payment_status || 'pending'} onValueChange={(v) => setPaymentStatus(detail.id, v)}>
                    <SelectTrigger className="mt-1" data-testid="admin-order-payment-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{nice(s)}</SelectItem>)}</SelectContent>
                  </Select>
                  {detail.payment_ref && <p className="mt-1 text-[11px] text-muted-foreground">Ref: {detail.payment_ref}</p>}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Customer</p>
                  <p className="mt-1 text-muted-foreground">{detail.address?.full_name}, {detail.address?.line1}, {detail.address?.city}, {detail.address?.state} - {detail.address?.pincode}</p>
                  <p className="text-muted-foreground">{detail.address?.phone}{detail.email ? ` · ${detail.email}` : ''}</p>
                </div>
              </div>

              {/* Shipping management */}
              <div className="rounded-xl border border-border p-3" data-testid="admin-order-shipping-panel">
                <p className="flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground"><Truck size={13} /> Shipping / Courier</p>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <div><label className="text-[11px] text-muted-foreground">Courier partner</label><input value={ship.courier} onChange={(e) => setShip((s) => ({ ...s, courier: e.target.value }))} placeholder="e.g. Delhivery, BlueDart" className={fieldCls} data-testid="admin-order-courier-input" /></div>
                  <div><label className="text-[11px] text-muted-foreground">Tracking / AWB number</label><input value={ship.tracking_number} onChange={(e) => setShip((s) => ({ ...s, tracking_number: e.target.value }))} placeholder="AWB number" className={fieldCls} data-testid="admin-order-awb-input" /></div>
                  <div><label className="text-[11px] text-muted-foreground">Shipping date</label><input type="date" value={ship.shipped_at} onChange={(e) => setShip((s) => ({ ...s, shipped_at: e.target.value }))} className={fieldCls} data-testid="admin-order-shipdate-input" /></div>
                  <div><label className="text-[11px] text-muted-foreground">Estimated delivery</label><input type="date" value={ship.estimated_delivery} onChange={(e) => setShip((s) => ({ ...s, estimated_delivery: e.target.value }))} className={fieldCls} data-testid="admin-order-eta-input" /></div>
                </div>
                <button onClick={saveShipping} disabled={savingShip} data-testid="admin-order-save-shipping" className="mt-3 inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"><Save size={12} /> {savingShip ? 'Saving…' : 'Save shipping details'}</button>
              </div>

              {detail.gift_message && <div><p className="font-semibold">Gift message</p><p className="text-muted-foreground">{detail.gift_message}</p></div>}
              {detail.gift_wrap && <div><p className="font-semibold">🎁 Gift wrapped {detail.gift_wrap_fee > 0 ? `(${inr(detail.gift_wrap_fee)})` : ''}</p>{detail.gift_note && <p className="text-muted-foreground">Note card: “{detail.gift_note}”</p>}</div>}
              {detail.order_notes && <div><p className="font-semibold">Customer note</p><p className="text-muted-foreground">{detail.order_notes}</p></div>}
              {detail.cancel_reason && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3" data-testid="admin-order-cancel-reason">
                  <p className="text-xs font-semibold uppercase text-red-700">Cancellation reason</p>
                  <p className="mt-1 text-red-800">{detail.cancel_reason}</p>
                </div>
              )}
              {detail.return_request && (
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-3" data-testid="admin-order-return-request">
                  <p className="text-xs font-semibold uppercase text-orange-700">Return request</p>
                  <p className="mt-1 text-orange-900">{detail.return_request.reason}</p>
                  {detail.return_request.images?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {detail.return_request.images.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt="" className="h-16 w-16 rounded-lg border border-orange-200 object-cover" />
                        </a>
                      ))}
                    </div>
                  )}
                  {detail.return_request.requested_at && (
                    <p className="mt-1 text-[11px] text-orange-700">Requested {new Date(detail.return_request.requested_at).toLocaleString()}</p>
                  )}
                </div>
              )}

              <div className="space-y-2">{detail.items.map((it, i) => (
                <div key={i} className={cx('flex items-center gap-3', it.cancelled && 'opacity-50')}>
                  <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <div className="flex-1">
                    <p className={cx('font-medium', it.cancelled && 'line-through')}>{it.name}</p>
                    <p className="text-xs text-muted-foreground">SKU {it.sku} · Qty {it.qty} · {inr(it.price)} each</p>
                    {it.cancelled && <p className="text-xs font-semibold text-red-600">Cancelled{it.cancel_reason ? ` — ${it.cancel_reason}` : ''}</p>}
                  </div>
                  <span className="font-semibold">{inr(it.line_total)}</span>
                </div>
              ))}</div>

              <div className="border-t border-border pt-3">
                <div className="flex justify-between"><span>Subtotal</span><span>{inr(detail.subtotal)}</span></div>
                {detail.discount > 0 && <div className="flex justify-between text-green-700"><span>Discount {detail.coupon_code ? `(${detail.coupon_code})` : ''}</span><span>-{inr(detail.discount)}</span></div>}
                <div className="flex justify-between"><span>Shipping</span><span>{detail.shipping === 0 ? 'Free' : inr(detail.shipping)}</span></div>
                {detail.gift_wrap_fee > 0 && <div className="flex justify-between"><span>Gift wrap</span><span>{inr(detail.gift_wrap_fee)}</span></div>}
                {detail.cod_fee > 0 && <div className="flex justify-between"><span>COD fee</span><span>{inr(detail.cod_fee)}</span></div>}
                <div className="flex justify-between font-bold"><span>Total</span><span>{inr(detail.total)}</span></div>
              </div>

              {/* Timeline */}
              {detail.timeline?.length > 0 && (
                <div><p className="text-xs font-semibold uppercase text-muted-foreground">Status history</p>
                  <ol className="mt-1 space-y-1.5" data-testid="admin-order-timeline">
                    {[...detail.timeline].reverse().map((e, i) => (
                      <li key={i} className="text-xs">
                        <span className="rounded-full bg-[hsl(var(--gaze-ivory))] px-2 py-0.5 capitalize">{nice(e.status)}</span>
                        <span className="ml-2 text-muted-foreground">{new Date(e.at).toLocaleString()}</span>
                        {e.note && <span className="ml-2 text-muted-foreground">— {e.note}</span>}
                        {e.by && <span className="ml-1 text-[10px] text-muted-foreground">({e.by})</span>}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Notifications (WhatsApp / Email delivery log) */}
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Notifications</p>
                {notifLogs.length > 0 ? (
                  <ul className="mt-1 space-y-1" data-testid="admin-order-notifications">
                    {notifLogs.map((n) => (
                      <li key={n.id} className="flex flex-wrap items-center gap-1.5 rounded-lg bg-[hsl(var(--gaze-ivory))] p-2 text-xs" data-testid="admin-order-notification-row">
                        <span className={
                          n.status === 'sent' ? 'rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-700'
                            : n.status === 'failed' ? 'rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-700'
                              : 'rounded-full bg-amber-100 px-2 py-0.5 font-semibold text-amber-700'
                        }>{n.status}</span>
                        <span className="font-medium capitalize">{(n.channel || '').replace('_', ' ')}</span>
                        <span className="capitalize text-muted-foreground">· {(n.event || '').replace('_', ' ')}</span>
                        {n.recipient && <span className="text-muted-foreground">· {n.recipient}</span>}
                        {n.reason && <span className="text-muted-foreground">— {n.reason}</span>}
                        <span className="ml-auto text-[10px] text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="mt-1 text-xs text-muted-foreground">No notifications recorded for this order yet.</p>}
              </div>

              {/* Admin notes */}
              <div>
                <p className="text-xs font-semibold uppercase text-muted-foreground">Internal notes</p>
                {(detail.admin_notes || []).length > 0 ? (
                  <ul className="mt-1 space-y-1">
                    {detail.admin_notes.map((n) => <li key={n.id} className="rounded-lg bg-[hsl(var(--gaze-ivory))] p-2 text-xs"><span className="font-medium">{n.author || 'admin'}:</span> {n.text}<span className="ml-2 text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span></li>)}
                  </ul>
                ) : <p className="mt-1 text-xs text-muted-foreground">No internal notes yet.</p>}
                <div className="mt-2 flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note" className="flex-1 rounded-lg border border-border px-3 py-2 text-xs" data-testid="admin-order-note-input" />
                  <button onClick={addNote} className="rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-xs font-semibold text-white" data-testid="admin-order-note-add">Add</button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}